import { ChangeEvent, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import InputAdornment from '@material-ui/core/InputAdornment';
import Drawer from '@base/Drawer/Drawer';
import Button from '@base/Button/Button';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import TextField from '@base/TextField/TextField';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import useInterval from '@hooks/useInterval';
import { Spacings, Fonts } from '@styles/index';
import LoginWrapper from '@cont/Login/Common/LoginWrapper';
import CountryMenu from '@cont/Login/CountryMenu';
import {
  PARENT_CONTACT_ANALYTICS_SOURCES,
  ParentContactAnalyticsSource,
  trackParentContactAdded,
  trackParentContactUpdated,
  trackParentOtpRequested,
  trackParentOtpSubmitted,
  trackParentOtpInvalid
} from '@stores/parent-contact/parentContactAnalytics';
import ParentContactVerificationSuccess from './ParentContactVerificationSuccess';
import {
  PARENT_CONTACT_STEPS,
  ParentContactCountry,
  ParentContactStep,
  formatPhoneDisplay,
  getCountryByCode,
  getCountryCodeForApi,
  getResponseMessage,
  PARENT_CONTACT_PHONE_MAX_LENGTH,
  validateNumber
} from './parentContactVerificationUtils';

interface ParentContactVerificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
  onStartLearning?: () => void;
  sendOtp: (params: { phone: string; countryCode: string }) => Promise<{
    otpSent?: boolean;
    otp_sent?: boolean;
    success?: boolean;
    message?: string;
    error?: { message?: string };
    statusCode?: number;
  }>;
  verifyOtp: (params: {
    phone: string;
    countryCode: string;
    otp: string;
  }) => Promise<{
    success?: boolean;
    message?: string;
    error?: { message?: string };
    statusCode?: number;
  }>;
  countries: ParentContactCountry[];
  initialPhone?: string;
  initialCountryCode?: string;
  analyticsSource?: ParentContactAnalyticsSource;
  isEditMode?: boolean;
  userHasSubscription?: boolean;
}

const StyledDrawer = styled(Drawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    grid-template-rows: 60px 1fr;
  }
`;

const StyledP1 = styled(P1)`
  margin-top: ${Spacings.SPACING_3B};
  color: var(--color-text-secondary);
`;

const EditDiv = styled.div`
  position: relative;
  margin-top: ${Spacings.SPACING_10B};
`;

const StyledTextField = styled(TextField)<any>`
  &.mui-focused {
    border: none;
  }
  &.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: var(--color-text-primary);
  }
  background: ${({ background }) => background};
  color: var(--color-text-primary);
`;

const OtpTextField = styled(TextField)<any>`
  margin-top: ${Spacings.SPACING_4B};
  color: var(--color-text-primary);
`;

const SaveButton = styled(Button)<any>`
  margin-top: ${Spacings.SPACING_8B};
`;

const VerifyOtpButton = styled(Button)<any>``;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacings.SPACING_8B};
`;

const StyledH6 = styled(H6)`
  font-weight: ${Fonts.BOLD};
`;

const ParentContactVerificationDrawer = ({
  isOpen,
  onClose,
  onVerified,
  onStartLearning,
  sendOtp,
  verifyOtp,
  countries,
  initialPhone = '',
  initialCountryCode,
  analyticsSource = PARENT_CONTACT_ANALYTICS_SOURCES.SETTINGS,
  isEditMode = false,
  userHasSubscription = false
}: ParentContactVerificationDrawerProps) => {
  const defaultCountry =
    countries?.find((c) => c.code?.toLowerCase() === 'in') ||
    countries?.[0] ||
    null;

  const [step, setStep] = useState<ParentContactStep>(
    PARENT_CONTACT_STEPS.PHONE
  );
  const [country, setCountry] = useState<ParentContactCountry | null>(
    defaultCountry
  );
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (countries?.length) {
      const countryToSelect =
        getCountryByCode(countries, initialCountryCode) ||
        country ||
        getCountryByCode(countries, 'in') ||
        countries[0];
      setCountry(countryToSelect);
    }
  }, [countries, country, initialCountryCode]);

  useEffect(() => {
    if (!isOpen) {
      setStep(PARENT_CONTACT_STEPS.PHONE);
      setOtp('');
      setPhone('');
      setErrorMessage('');
      setTimeLeft(0);
      return;
    }

    setStep(PARENT_CONTACT_STEPS.PHONE);
    setOtp('');
    setPhone(initialPhone || '');
    setErrorMessage('');
    setTimeLeft(0);

    const countryToSelect =
      getCountryByCode(countries, initialCountryCode) ||
      getCountryByCode(countries, 'in') ||
      countries?.[0] ||
      country;
    setCountry(countryToSelect);
  }, [countries, country, initialCountryCode, initialPhone, isOpen]);

  const isResendDisabled = timeLeft > 0;

  const startTimer = () => setTimeLeft(60);

  useInterval(
    () => setTimeLeft((prev) => prev - 1),
    timeLeft > 0 ? 1000 : null
  );

  const getTimeLeftStr = () => {
    if (timeLeft > 0) {
      const str = `0${timeLeft}`;
      return ` in 00:${String(str).slice(-2)}`;
    }
    return '';
  };

  const handleSendOtp = async () => {
    if (!phone || !country) return;
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await sendOtp({
        phone,
        countryCode: getCountryCodeForApi(country)
      });
      const hasOtpSendFailed =
        Boolean(response?.error) ||
        Boolean(response?.statusCode && response.statusCode >= 400) ||
        response?.otpSent === false ||
        response?.otp_sent === false ||
        response?.success === false;

      if (!hasOtpSendFailed) {
        trackParentOtpRequested({
          source: analyticsSource,
          userHasSubscription
        });
        setStep(PARENT_CONTACT_STEPS.OTP);
        startTimer();
      } else {
        setErrorMessage(getResponseMessage(response) || 'Unable to send OTP');
      }
    } catch {
      setErrorMessage('Unable to send OTP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!phone || !country || !otp) return;
    trackParentOtpSubmitted({
      source: analyticsSource,
      userHasSubscription
    });
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await verifyOtp({
        phone,
        countryCode: getCountryCodeForApi(country),
        otp
      });

      const hasOtpVerifyFailed =
        Boolean(response?.error) ||
        Boolean(response?.statusCode && response.statusCode >= 400) ||
        response?.success === false;

      if (!hasOtpVerifyFailed) {
        if (isEditMode) {
          trackParentContactUpdated({
            source: analyticsSource,
            userHasSubscription
          });
        } else {
          trackParentContactAdded({
            source: analyticsSource,
            userHasSubscription
          });
        }
        setStep(PARENT_CONTACT_STEPS.SUCCESS);
      } else {
        trackParentOtpInvalid({
          source: analyticsSource,
          userHasSubscription
        });
        setErrorMessage(
          getResponseMessage(response) || 'Invalid OTP. Please try again.'
        );
      }
    } catch {
      trackParentOtpInvalid({
        source: analyticsSource,
        userHasSubscription
      });
      setErrorMessage('Invalid OTP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendClick = () => {
    if (!isResendDisabled) {
      handleSendOtp();
    }
  };

  const isPaymentTouchpoint =
    analyticsSource === PARENT_CONTACT_ANALYTICS_SOURCES.POST_PAYMENT;

  const handleClose = () => {
    if (step === PARENT_CONTACT_STEPS.SUCCESS) {
      onVerified();
      onClose();
      return;
    }
    onClose();
  };

  const handleContinueSuccess = () => {
    onVerified();
    onClose();
  };

  const onFieldValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitized = value.replace(/ /g, '');
    if (name === 'mobile') {
      if (sanitized.match(/^\d+$/) || sanitized === '') {
        setPhone(sanitized.slice(0, PARENT_CONTACT_PHONE_MAX_LENGTH));
      }
    } else if (name === 'otp') {
      if (sanitized.match(/^\d+$/) || sanitized === '') {
        setOtp(sanitized);
      }
    }
    setErrorMessage('');
  };

  const countryMenu = country ? (
    <InputAdornment position="start">
      <CountryMenu
        disabled={step === PARENT_CONTACT_STEPS.OTP}
        changeCountry={(option) => {
          const newCountry = countries.find((c) => c.code === option.value);
          setCountry(newCountry ?? null);
        }}
        country={country}
        countries={countries}
      />
    </InputAdornment>
  ) : null;

  const renderPhoneStep = () => (
    <>
      <H2 color="var(--color-text-primary)">
        {step === PARENT_CONTACT_STEPS.OTP
          ? 'Enter OTP'
          : "Enter parent's whatsapp number"}
      </H2>
      <StyledP1>
        {step === PARENT_CONTACT_STEPS.OTP
          ? `We've sent an OTP to ${formatPhoneDisplay(country, phone)}`
          : 'To enable WhatsApp updates'}
      </StyledP1>
      <EditDiv>
        <StyledTextField
          name="mobile"
          fullWidth
          startAdornment={countryMenu}
          placeholder="Enter your parent's whatsapp number"
          onChange={onFieldValueChange}
          value={phone}
          inputProps={{
            type: 'tel',
            maxLength: PARENT_CONTACT_PHONE_MAX_LENGTH,
            minLength: PARENT_CONTACT_PHONE_MAX_LENGTH,
            required: true
          }}
          onInput={(event) =>
            validateNumber(event, PARENT_CONTACT_PHONE_MAX_LENGTH)
          }
          type="tel"
          autoFocus={step === PARENT_CONTACT_STEPS.PHONE}
          disabled={step === PARENT_CONTACT_STEPS.OTP}
          error={Boolean(errorMessage) && step === PARENT_CONTACT_STEPS.PHONE}
          helperText={step === PARENT_CONTACT_STEPS.PHONE ? errorMessage : ''}
          background={
            step === PARENT_CONTACT_STEPS.OTP
              ? 'var(--color-divider)'
              : 'var(--color-base-1)'
          }
        />
      </EditDiv>
      {step === PARENT_CONTACT_STEPS.OTP && (
        <OtpTextField
          name="otp"
          fullWidth
          placeholder="Enter OTP"
          onChange={onFieldValueChange}
          value={otp}
          inputProps={{
            type: 'tel',
            maxLength: 6,
            required: true
          }}
          onInput={(event) => validateNumber(event, 6)}
          type="tel"
          autoFocus
          error={Boolean(errorMessage)}
          helperText={errorMessage}
        />
      )}
      {step === PARENT_CONTACT_STEPS.PHONE ? (
        <SaveButton
          variant={
            phone.length !== PARENT_CONTACT_PHONE_MAX_LENGTH || isSubmitting
              ? 'disabled'
              : 'filled'
          }
          label={!isSubmitting ? 'Continue' : 'Sending OTP'}
          onClick={
            phone.length === PARENT_CONTACT_PHONE_MAX_LENGTH && !isSubmitting
              ? handleSendOtp
              : null
          }
          color="green"
        />
      ) : (
        <ButtonWrapper>
          <VerifyOtpButton
            variant={!otp || isSubmitting ? 'disabled' : 'filled'}
            label={!isSubmitting ? 'Verify OTP' : 'Verifying OTP'}
            onClick={!isSubmitting ? handleVerifyOtp : null}
            color="green"
          />
          <UnderlineLink
            disabled={isResendDisabled}
            onClick={handleResendClick}
            color={
              isResendDisabled
                ? 'var(--color-text-secondary)'
                : 'var(--color-text-primary)'
            }
          >
            <StyledH6
              color={
                isResendDisabled
                  ? 'var(--color-text-secondary)'
                  : 'var(--color-text-primary)'
              }
            >
              Resend OTP{getTimeLeftStr()}
            </StyledH6>
          </UnderlineLink>
        </ButtonWrapper>
      )}
    </>
  );

  if (!isOpen) return null;

  return (
    <StyledDrawer login drawerState={isOpen} onClose={handleClose}>
      <LoginWrapper>
        {!country ? (
          <P1 color="var(--color-text-primary)">Loading...</P1>
        ) : (
          <>
            {step === PARENT_CONTACT_STEPS.SUCCESS ? (
              <ParentContactVerificationSuccess
                country={country}
                phone={phone}
                onContinue={handleContinueSuccess}
              />
            ) : (
              renderPhoneStep()
            )}
          </>
        )}
      </LoginWrapper>
    </StyledDrawer>
  );
};

export default ParentContactVerificationDrawer;
